import React, { useState, useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import {
  Box,
  Typography,
  InputAdornment,
  TextField,
  Tooltip,
  Paper,
  Stack,
  Link,
  Chip,
  Avatar,
  alpha,
  useTheme,
  LinearProgress,
  useMediaQuery,
  Pagination,
} from '@mui/material'
import {
  Search as SearchIcon,
  InfoOutlined as InfoIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material'
import { useTenantContext } from '@/context'
import { createGuestAppointmentSchema } from '@/constants'
import { extractValidationErrors } from '@/utils'
import {
  CommonDialog,
  CommonButton,
  CommonTextField,
  CommonPhone,
} from '@/components/common'

const SEARCH_CRITERIA = [
  'Donor last name',
  'Donor ID number',
  'Donor phone number',
  'Donor email address',
  'Donor date of birth',
]

const getDonorDisplayName = donor => {
  if (donor?.last_name != null || donor?.first_name != null) {
    return [donor.last_name, donor.first_name].filter(Boolean).join(', ')
  }
  return donor?.name || donor?.full_name || 'Unknown'
}

const getDonorNumber = donor =>
  donor?.donor_number != null
    ? String(donor.donor_number)
    : (donor?.donor_id ?? '—')

const getDonorPhone = donor => donor?.primary_phone || donor?.phone_number || ''

const formatUSPhone = raw => {
  if (!raw) return ''
  const digits = String(raw).replace(/\D/g, '')
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
  }
  if (digits.length === 11 && digits[0] === '1') {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`
  }
  return raw
}

const getDonorAddress = donor => {
  const parts = [
    donor?.address1,
    donor?.address2,
    [donor?.address_city, donor?.address_state].filter(Boolean).join(', '),
    donor?.zip_code,
  ].filter(Boolean)
  return parts.join(' ') || '—'
}

const getDonorInitials = donor => {
  const name = getDonorDisplayName(donor)
  const parts = name.split(/[\s,]+/).filter(Boolean)
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase().slice(0, 2)
  }
  return name.slice(0, 2).toUpperCase() || '?'
}

const getDonorKey = donor =>
  donor?.donor_id ?? donor?.id ?? String(donor?.donor_number ?? '')

export const DonorSearchModal = ({
  open,
  onClose,
  slotId,
  slotTime,
  onConfirmSuccess,
  searchDonors,
  scheduleDonor,
  loadingSearch,
  loadingConfirm,
  scheduleGuest,
  loadingScheduleGuest,
}) => {
  const SEARCH_LIMIT = 5
  const { tenant } = useTenantContext()
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [totalResults, setTotalResults] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [hasSearched, setHasSearched] = useState(false)
  const [selectedDonor, setSelectedDonor] = useState(null)
  const [error, setError] = useState(null)
  const [notifyChoiceOpen, setNotifyChoiceOpen] = useState(false)
  const [view, setView] = useState('search')
  const [guestForm, setGuestForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
  })
  const [guestFormErrors, setGuestFormErrors] = useState({})
  const [guestNotifyOpen, setGuestNotifyOpen] = useState(false)

  const isEmailRequired = useMemo(
    () =>
      tenant?.configuration_options?.find(
        item => item.code === 'DP_GUEST_EMAIL_REQUIRED'
      )?.value === 'true',
    [tenant]
  )
  const isPhoneRequired = useMemo(
    () =>
      tenant?.configuration_options?.find(
        item => item.code === 'DP_GUEST_PHONE_REQUIRED'
      )?.value === 'true',
    [tenant]
  )
  const isEmailOrPhoneRequired = useMemo(
    () =>
      tenant?.configuration_options?.find(
        item => item.code === 'DP_GUEST_EMAIL_OR_PHONE_REQUIRED'
      )?.value === 'true',
    [tenant]
  )
  const guestSchema = useMemo(
    () =>
      createGuestAppointmentSchema(
        isEmailRequired,
        isPhoneRequired,
        isEmailOrPhoneRequired
      ),
    [isEmailRequired, isPhoneRequired, isEmailOrPhoneRequired]
  )

  const handleClose = useCallback(() => {
    setQuery('')
    setResults([])
    setTotalResults(0)
    setCurrentPage(1)
    setHasSearched(false)
    setSelectedDonor(null)
    setError(null)
    setNotifyChoiceOpen(false)
    setView('search')
    setGuestForm({ firstName: '', lastName: '', email: '', phoneNumber: '' })
    setGuestFormErrors({})
    setGuestNotifyOpen(false)
    onClose()
  }, [onClose])

  const runSearch = useCallback(
    async (searchQuery, page = 1) => {
      const payload = await searchDonors(searchQuery, page, SEARCH_LIMIT)
      const donors = payload?.donors ?? (Array.isArray(payload) ? payload : [])
      const total = payload?.total ?? donors.length
      setResults(donors)
      setTotalResults(total)
      setCurrentPage(page)
      return { donors, total }
    },
    [searchDonors]
  )

  const handleSearch = useCallback(
    async e => {
      if (e) e.preventDefault()
      if (!query?.trim()) return
      setError(null)
      setHasSearched(true)
      setSelectedDonor(null)
      try {
        await runSearch(query.trim(), 1)
      } catch (err) {
        setError('Search failed. Please try again.')
        setResults([])
        setTotalResults(0)
        setCurrentPage(1)
      }
    },
    [query, runSearch]
  )

  const handlePageChange = useCallback(
    (_, page) => {
      if (!query?.trim()) return
      setError(null)
      runSearch(query.trim(), page)
    },
    [query, runSearch]
  )

  const handleKeyDown = e => {
    if (e.key === 'Enter') handleSearch(e)
  }

  const handleConfirm = useCallback(
    async notifyDonor => {
      const donorId = selectedDonor?.donor_id ?? selectedDonor?.id
      if (!donorId || !slotId) return
      setNotifyChoiceOpen(false)
      setError(null)
      try {
        const response = await scheduleDonor(donorId, slotId, notifyDonor)
        if (response?.status === 201 || response?.data != null) {
          const donorName = getDonorDisplayName(selectedDonor)
          handleClose()
          onConfirmSuccess?.({
            name: donorName,
            time: slotTime,
          })
        } else {
          setError(response?.message || 'Failed to schedule appointment.')
        }
      } catch (err) {
        setError(
          err?.response?.data?.message || 'Failed to schedule appointment.'
        )
      }
    },
    [
      selectedDonor,
      slotId,
      scheduleDonor,
      slotTime,
      onConfirmSuccess,
      handleClose,
    ]
  )

  const handleGuestSubmit = useCallback(
    async e => {
      if (e) e.preventDefault()
      if (!scheduleGuest) return
      setGuestFormErrors({})
      setError(null)
      try {
        await guestSchema.validate(guestForm, { abortEarly: false })
      } catch (err) {
        const fieldErrors = extractValidationErrors(err)
        if (fieldErrors) {
          setGuestFormErrors(fieldErrors)
          return
        }
      }
      if (guestForm.email) {
        setGuestNotifyOpen(true)
      } else {
        handleGuestConfirm(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scheduleGuest, guestSchema, guestForm]
  )

  const handleGuestConfirm = useCallback(
    async notifyDonor => {
      setGuestNotifyOpen(false)
      setError(null)
      try {
        const response = await scheduleGuest({
          slot_id: slotId,
          first_name: guestForm.firstName,
          last_name: guestForm.lastName,
          email: guestForm.email || undefined,
          phone_number: guestForm.phoneNumber || undefined,
          notify_donor: notifyDonor,
        })
        if (response?.data != null || response?.status === 201) {
          const guestName =
            `${guestForm.firstName} ${guestForm.lastName}`.trim()
          handleClose()
          onConfirmSuccess?.({ name: guestName, time: slotTime })
        } else {
          setError(response?.message || 'Failed to schedule guest.')
        }
      } catch (err) {
        const fieldErrors = extractValidationErrors(err)
        if (fieldErrors) {
          setGuestFormErrors(fieldErrors)
        } else {
          setError(err?.response?.data?.message || 'Failed to schedule guest.')
        }
      }
    },
    [scheduleGuest, guestForm, slotId, slotTime, handleClose, onConfirmSuccess]
  )

  const showNoResults =
    hasSearched && !loadingSearch && results.length === 0 && !error
  const showResults = hasSearched && results.length > 0
  const showConfirmButton = selectedDonor != null
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  return (
    <>
      <CommonDialog
        open={open}
        onClose={handleClose}
        title={null}
        maxWidth='md'
        fullWidth
        PaperProps={{
          sx: {
            minWidth: { xs: '95vw', sm: 560 },
            maxWidth: 680,
            height: 600,
            maxHeight: '90vh',
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 24px 48px rgba(0,0,0,0.12)',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            minHeight: 0,
          }}
        >
          {/* Spacer for dialog close button (absolute top-right) */}
          <Box sx={{ height: 10, flexShrink: 0 }} aria-hidden />
          {/* Header strip with slot context */}
          <Box
            sx={{
              px: 3,
              pt: 2,
              pb: 2,
              flexShrink: 0,
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Stack
              direction='row'
              alignItems='center'
              justifyContent='space-between'
              flexWrap='wrap'
              gap={1.5}
            >
              <Stack direction='row' alignItems='center' gap={1.5}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <PersonIcon fontSize='small' />
                </Box>
                <Box>
                  <Typography
                    variant='h6'
                    fontWeight={700}
                    color='text.primary'
                  >
                    {view === 'guest_form'
                      ? 'Schedule as Guest'
                      : 'Find a donor'}
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                    {view === 'guest_form'
                      ? 'Fill in the guest details to book this slot'
                      : 'Search and select to schedule'}
                  </Typography>
                </Box>
              </Stack>
              {slotTime && (
                <Chip
                  icon={<ScheduleIcon sx={{ fontSize: 18 }} />}
                  label={slotTime}
                  size='small'
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                    color: 'primary.dark',
                    fontWeight: 600,
                    '& .MuiChip-icon': { color: 'inherit' },
                  }}
                />
              )}
            </Stack>

            {/* Search bar + mobile search button */}
            {view === 'search' && (
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                spacing={1.5}
                sx={{ mt: 2 }}
              >
                <TextField
                  size='medium'
                  placeholder='Search by name, ID, phone, email, or DOB…'
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={loadingSearch}
                  fullWidth
                  autoFocus
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'background.paper',
                      borderRadius: 2,
                      boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                      '&:hover': {
                        boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.25)}`,
                      },
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position='start' sx={{ ml: 0.5 }}>
                        <SearchIcon sx={{ color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                    endAdornment: !isMobile ? (
                      <InputAdornment position='end'>
                        <Tooltip
                          title='Search by donor last name, ID, phone, email, or date of birth'
                          arrow
                        >
                          <InfoIcon
                            sx={{
                              color: 'text.secondary',
                              cursor: 'help',
                              fontSize: 20,
                            }}
                          />
                        </Tooltip>
                      </InputAdornment>
                    ) : undefined,
                  }}
                />
                {isMobile && (
                  <CommonButton
                    variant='primary'
                    label='Search'
                    onClick={() => handleSearch()}
                    disabled={loadingSearch || !query?.trim()}
                    startIcon={<SearchIcon />}
                    sx={{ flexShrink: 0 }}
                  />
                )}
              </Stack>
            )}
          </Box>

          {/* Criteria list (before search) */}
          {view === 'search' && !hasSearched && (
            <Box sx={{ px: 3, py: 2, flexShrink: 0 }}>
              <Typography
                variant='body2'
                color='text.secondary'
                sx={{ mb: 1, display: 'block' }}
              >
                Search by:
              </Typography>
              <Box
                component='ul'
                sx={{ m: 0, pl: 2.5, listStyle: 'disc', '& li': { mb: 0.5 } }}
              >
                {SEARCH_CRITERIA.map((item, idx) => (
                  <Typography
                    key={idx}
                    component='li'
                    variant='body2'
                    color='text.secondary'
                  >
                    {item}
                  </Typography>
                ))}
              </Box>
              <Typography
                variant='body2'
                color='text.secondary'
                sx={{ mt: 2, mb: 1, display: 'block' }}
              >
                Or:
              </Typography>
              <Box component='ul' sx={{ m: 0, pl: 2.5, listStyle: 'disc' }}>
                <Typography
                  component='li'
                  variant='body2'
                  color='text.secondary'
                >
                  <Link
                    component='button'
                    type='button'
                    variant='body2'
                    onClick={() => {
                      setError(null)
                      setView('guest_form')
                    }}
                    sx={{
                      color: 'primary.main',
                      cursor: 'pointer',
                      fontWeight: 600,
                      background: 'none',
                      border: 'none',
                      font: 'inherit',
                      p: 0,
                      '&:hover': { color: 'primary.dark' },
                    }}
                  >
                    Schedule as guest
                  </Link>
                </Typography>
              </Box>
            </Box>
          )}

          {/* Loading */}
          {view === 'search' && loadingSearch && (
            <Box sx={{ px: 3, py: 2, flexShrink: 0 }}>
              <LinearProgress sx={{ borderRadius: 1, height: 6 }} />
              <Typography
                variant='body2'
                color='text.secondary'
                sx={{ mt: 1.5, textAlign: 'center' }}
              >
                Searching donors…
              </Typography>
            </Box>
          )}

          {/* No results */}
          {view === 'search' && showNoResults && (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                pt: 4,
                px: 3,
                pb: 3,
                textAlign: 'center',
                bgcolor: alpha(theme.palette.primary.main, 0.02),
                borderRadius: 2,
                mx: 3,
                my: 2,
              }}
            >
              <Stack alignItems='center' spacing={1.5} sx={{ flexShrink: 0 }}>
                <PersonIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
                <Typography
                  variant='subtitle1'
                  fontWeight={600}
                  color='text.primary'
                >
                  No matching donor records found
                </Typography>
                <Typography
                  variant='body2'
                  color='text.secondary'
                  sx={{ maxWidth: 320 }}
                >
                  Try a different search, or{' '}
                  <Link
                    component='button'
                    type='button'
                    variant='body2'
                    onClick={() => {
                      setError(null)
                      setView('guest_form')
                    }}
                    sx={{
                      color: 'primary.main',
                      cursor: 'pointer',
                      fontWeight: 600,
                      background: 'none',
                      border: 'none',
                      font: 'inherit',
                      p: 0,
                      '&:hover': { color: 'primary.dark' },
                    }}
                  >
                    schedule as guest
                  </Link>
                </Typography>
              </Stack>

              <Typography
                variant='caption'
                fontWeight={400}
                color='text.primary'
                sx={{ mt: 6, textAlign: 'center' }}
              >
                Scheduling is limited to donors who have donated at this blood
                drive in the past.
              </Typography>
            </Box>
          )}

          {/* Results list */}
          {view === 'search' && showResults && (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                px: 3,
                py: 2,
                mb: showConfirmButton ? 0 : 2,
              }}
            >
              <Typography
                variant='caption'
                color='text.secondary'
                fontWeight={600}
                sx={{ display: 'block', mb: 1.5 }}
              >
                {totalResults > 0
                  ? `${totalResults} result${totalResults !== 1 ? 's' : ''}`
                  : `${results.length} result${results.length !== 1 ? 's' : ''}`}
              </Typography>
              <Stack spacing={1.25}>
                {results.map(donor => {
                  const donorKey = getDonorKey(donor)
                  const selectedKey =
                    selectedDonor != null ? getDonorKey(selectedDonor) : null
                  const isSelected = selectedKey === donorKey
                  return (
                    <Paper
                      key={donorKey}
                      elevation={0}
                      onClick={() =>
                        setSelectedDonor(prev =>
                          prev != null && getDonorKey(prev) === donorKey
                            ? null
                            : donor
                        )
                      }
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: '2px solid',
                        borderColor: isSelected ? 'primary.main' : 'divider',
                        bgcolor: isSelected
                          ? alpha(theme.palette.primary.main, 0.06)
                          : 'grey.50',
                        cursor: 'pointer',
                        transition: 'border-color 0.2s, background-color 0.2s',
                        '&:hover': {
                          borderColor: isSelected ? 'primary.main' : 'grey.400',
                          bgcolor: isSelected
                            ? alpha(theme.palette.primary.main, 0.08)
                            : 'grey.100',
                        },
                      }}
                    >
                      <Stack direction='row' alignItems='center' gap={2}>
                        <Avatar
                          sx={{
                            width: 44,
                            height: 44,
                            bgcolor: 'primary.main',
                            color: 'white',
                            fontWeight: 700,
                            fontSize: '0.9rem',
                          }}
                        >
                          {getDonorInitials(donor)}
                        </Avatar>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography
                            variant='caption'
                            color='text.secondary'
                            display='block'
                          >
                            Donor #{getDonorNumber(donor)}
                          </Typography>
                          <Typography
                            variant='subtitle1'
                            fontWeight={600}
                            sx={{ mt: 0.25 }}
                            component='span'
                          >
                            {getDonorDisplayName(donor)}
                          </Typography>
                          {getDonorPhone(donor) && (
                            <Typography
                              variant='body2'
                              color='text.secondary'
                              component='span'
                              sx={{ ml: 1.5 }}
                            >
                              {formatUSPhone(getDonorPhone(donor))}
                            </Typography>
                          )}
                          <Typography
                            variant='body2'
                            color='text.secondary'
                            sx={{ mt: 0.25 }}
                            noWrap
                          >
                            {getDonorAddress(donor)}
                          </Typography>
                        </Box>
                        <CommonButton
                          variant={isSelected ? 'primary' : 'secondary'}
                          size='md'
                          onClick={e => {
                            e.stopPropagation()
                            setSelectedDonor(prev =>
                              prev != null && getDonorKey(prev) === donorKey
                                ? null
                                : donor
                            )
                          }}
                          sx={{ flexShrink: 0 }}
                        >
                          {isSelected ? 'Selected' : 'Select'}
                        </CommonButton>
                      </Stack>
                    </Paper>
                  )
                })}
              </Stack>
              {totalResults > SEARCH_LIMIT && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    mt: 2,
                    pb: 1,
                  }}
                >
                  <Pagination
                    count={Math.ceil(totalResults / SEARCH_LIMIT)}
                    page={currentPage}
                    onChange={handlePageChange}
                    color='primary'
                    size='small'
                    showFirstButton
                    showLastButton
                  />
                </Box>
              )}
              <Typography
                variant='body2'
                color='text.secondary'
                sx={{ mt: 2, textAlign: 'center' }}
              >
                Can't find them?{' '}
                <Link
                  component='button'
                  type='button'
                  variant='body2'
                  onClick={() => {
                    setError(null)
                    setView('guest_form')
                  }}
                  sx={{
                    color: 'primary.main',
                    cursor: 'pointer',
                    fontWeight: 600,
                    background: 'none',
                    border: 'none',
                    font: 'inherit',
                    p: 0,
                    '&:hover': { color: 'primary.dark' },
                  }}
                >
                  schedule as guest
                </Link>
              </Typography>
            </Box>
          )}

          {/* Guest form body */}
          {view === 'guest_form' && (
            <Box
              component='form'
              onSubmit={handleGuestSubmit}
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                px: 3,
                py: 2,
                mt: 1.5,
                display: 'flex',
                flexDirection: 'column',
                gap: 2.5,
              }}
            >
              <CommonTextField
                id='guest-first-name'
                label='First Name'
                value={guestForm.firstName}
                setValue={value =>
                  setGuestForm(f => ({ ...f, firstName: value }))
                }
                type='text'
                required
                error={guestFormErrors.firstName}
                placeholder='Enter first name'
                inputProps={{ name: 'firstName', autoComplete: 'given-name' }}
              />
              <CommonTextField
                id='guest-last-name'
                label='Last Name'
                value={guestForm.lastName}
                setValue={value =>
                  setGuestForm(f => ({ ...f, lastName: value }))
                }
                type='text'
                required
                error={guestFormErrors.lastName}
                placeholder='Enter last name'
                inputProps={{ name: 'lastName', autoComplete: 'family-name' }}
              />
              <CommonTextField
                id='guest-email'
                label='Email'
                value={guestForm.email}
                setValue={value => setGuestForm(f => ({ ...f, email: value }))}
                type='email'
                error={guestFormErrors.email}
                required={isEmailRequired}
                placeholder={
                  isEmailRequired
                    ? 'Enter email'
                    : isEmailOrPhoneRequired
                      ? 'Enter email (email or phone required)'
                      : 'Enter email (optional)'
                }
                inputProps={{
                  name: 'email',
                  autoComplete: 'username email',
                }}
              />
              <CommonPhone
                id='guest-phone-number'
                label='Phone Number'
                value={guestForm.phoneNumber}
                setValue={value =>
                  setGuestForm(f => ({ ...f, phoneNumber: value }))
                }
                mask='(999) 999-9999'
                required={isPhoneRequired}
                error={guestFormErrors.phoneNumber}
                placeholder={
                  isPhoneRequired
                    ? '(###)-###-####'
                    : isEmailOrPhoneRequired
                      ? '(###)-###-#### (email or phone required)'
                      : '(###)-###-#### (optional)'
                }
                inputProps={{ name: 'phoneNumber' }}
              />
              {isEmailOrPhoneRequired &&
                !isEmailRequired &&
                !isPhoneRequired && (
                  <Typography
                    variant='body2'
                    color='text.secondary'
                    sx={{ mt: -1, textAlign: 'center' }}
                  >
                    Please provide either an email address or phone number
                  </Typography>
                )}
            </Box>
          )}

          {/* Error (shared) */}
          {error && (
            <Box
              sx={{
                mx: 3,
                mt: 2,
                p: 1.5,
                flexShrink: 0,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.error.main, 0.08),
                border: '1px solid',
                borderColor: alpha(theme.palette.error.main, 0.3),
              }}
            >
              <Typography variant='body2' color='error.main' fontWeight={500}>
                {error}
              </Typography>
            </Box>
          )}

          {/* Confirm footer */}
          {view === 'search' && showConfirmButton && (
            <Box
              sx={{
                flexShrink: 0,
                px: 3,
                py: 2,
                borderTop: '1px solid',
                borderColor: 'divider',
                bgcolor: alpha(theme.palette.primary.main, 0.04),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 1.5,
              }}
            >
              <Typography variant='body2' color='text.secondary'>
                Scheduling <strong>{getDonorDisplayName(selectedDonor)}</strong>{' '}
                for this slot
              </Typography>
              <CommonButton
                variant='primary'
                label='Confirm schedule'
                loading={loadingConfirm}
                disabled={loadingConfirm}
                onClick={() => setNotifyChoiceOpen(true)}
                sx={{ minWidth: 160 }}
              />
            </Box>
          )}

          {/* Guest form footer */}
          {view === 'guest_form' && (
            <Box
              sx={{
                flexShrink: 0,
                px: 3,
                py: 2,
                borderTop: '1px solid',
                borderColor: 'divider',
                bgcolor: alpha(theme.palette.primary.main, 0.04),
                display: 'flex',
                gap: 1.5,
                justifyContent: 'flex-end',
              }}
            >
              <CommonButton
                variant='secondary'
                label='Back'
                disabled={loadingScheduleGuest}
                onClick={() => {
                  setView('search')
                  setGuestFormErrors({})
                  setError(null)
                }}
              />
              <CommonButton
                variant='primary'
                label='Book Appointment'
                loading={loadingScheduleGuest}
                disabled={loadingScheduleGuest}
                onClick={handleGuestSubmit}
                sx={{ minWidth: 160 }}
              />
            </Box>
          )}
        </Box>
      </CommonDialog>

      {/* Notify choice popup */}
      <CommonDialog
        open={notifyChoiceOpen}
        onClose={() => !loadingConfirm && setNotifyChoiceOpen(false)}
        title='How would you like to schedule?'
        maxWidth='xs'
        fullWidth
        persistent={loadingConfirm}
      >
        <Stack spacing={1.5} sx={{ pt: 1, pb: 2 }}>
          <CommonButton
            variant='primary'
            label='Schedule and Notify'
            loading={loadingConfirm}
            disabled={loadingConfirm}
            onClick={() => handleConfirm(true)}
            fullWidth
          />
          <CommonButton
            variant='secondary'
            label='Schedule without Notifying'
            disabled={loadingConfirm}
            onClick={() => handleConfirm(false)}
            fullWidth
          />
        </Stack>
      </CommonDialog>

      {/* Guest notify dialog */}
      <CommonDialog
        open={guestNotifyOpen}
        onClose={() => !loadingScheduleGuest && setGuestNotifyOpen(false)}
        title='Notify the guest?'
        maxWidth='xs'
        fullWidth
        persistent={loadingScheduleGuest}
      >
        <Stack spacing={1.5} sx={{ pt: 1, pb: 2 }}>
          <CommonButton
            variant='primary'
            label='Schedule and Notify Guest'
            loading={loadingScheduleGuest}
            disabled={loadingScheduleGuest}
            onClick={() => handleGuestConfirm(true)}
            fullWidth
          />
          <CommonButton
            variant='secondary'
            label='Schedule without Notifying'
            disabled={loadingScheduleGuest}
            onClick={() => handleGuestConfirm(false)}
            fullWidth
          />
        </Stack>
      </CommonDialog>
    </>
  )
}

DonorSearchModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  slotId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  slotTime: PropTypes.string,
  onConfirmSuccess: PropTypes.func,
  searchDonors: PropTypes.func.isRequired,
  scheduleDonor: PropTypes.func.isRequired,
  loadingSearch: PropTypes.bool,
  loadingConfirm: PropTypes.bool,
  scheduleGuest: PropTypes.func,
  loadingScheduleGuest: PropTypes.bool,
}
